import React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useLogout } from '../../components/modules/requests/logOut'
import Logo from '../../components/generic/imageComponents/Logo'
import LangSwitcher from '../../components/generic/particles/LangSwitcher'

interface Props {
  collapsed?: boolean
  showSettings?: boolean
  onItemClick?: () => void
}

const LogoutGlyph = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
    <polyline points='16 17 21 12 16 7' />
    <line x1='21' y1='12' x2='9' y2='12' />
  </svg>
)

const CogGlyph = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <circle cx='12' cy='12' r='3' />
    <path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z' />
  </svg>
)

const SidebarFooter: React.FC<Props> = ({
  collapsed,
  showSettings,
  onItemClick,
}) => {
  const token = useSelector((s: any) => s.mainReducer.token)
  const logout = useLogout()

  return (
    <Foot $collapsed={!!collapsed}>
      {!collapsed && (
        <BrandSlot>
          <Logo $size='sm' />
        </BrandSlot>
      )}
      <Actions $collapsed={!!collapsed}>
        <LangSwitcher variant='initials' dropDirection='up' chip />
        {showSettings && (
          <SettingsChip
            to='/main/settings/account'
            onClick={onItemClick}
            aria-label='הגדרות'
          >
            <CogGlyph />
          </SettingsChip>
        )}
        <LogoutChip
          type='button'
          onClick={() => {
            logout(token)
            onItemClick?.()
          }}
          aria-label='התנתק'
        >
          <LogoutGlyph />
        </LogoutChip>
      </Actions>
    </Foot>
  )
}

export default SidebarFooter

const Foot = styled.div<{ $collapsed: boolean }>`
  flex-shrink: 0;
  padding: ${({ $collapsed }) => ($collapsed ? '12px 8px' : '12px 14px 14px')};
  border-top: 1px solid ${({ theme }) => theme.colors.line2};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'space-between')};
  flex-direction: ${({ $collapsed }) => ($collapsed ? 'column' : 'row')};
  gap: 8px;
  direction: rtl;
`

const BrandSlot = styled.div`
  display: flex;
  align-items: center;
  height: 2.25rem;
  flex: 0 0 auto;
  opacity: 0.7;

  a { height: auto; display: flex; align-items: center; }
  img { min-width: 0; width: 88px; max-width: 100%; display: block; }
`

const Actions = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-direction: ${({ $collapsed }) => ($collapsed ? 'column' : 'row')};
`

const chipBase = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const SettingsChip = styled(Link)`
  ${chipBase}
  background: transparent;
  color: ${({ theme }) => theme.colors.icon};
  border: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.ink};
  }
`

const LogoutChip = styled.button`
  ${chipBase}
  background: transparent;
  color: ${({ theme }) => theme.colors.danger};
  border: 0;

  &:hover:not(:disabled) {
    color: #DC2626;
  }
`
